import { Play, Pause, Heart } from "lucide-react";
import { usePlayer, type Track } from "@/stores/player";
import { api } from "@/lib/api";
import { useState } from "react";
import { toast } from "sonner";

function fmt(s?: number) {
  if (!s) return "—";
  const m = Math.floor(s / 60);
  const r = Math.floor(s % 60);
  return `${m}:${r.toString().padStart(2, "0")}`;
}

export function TrackRow({
  track,
  index,
  queue,
  isFavorite: initialFav,
  onUnfavorite,
}: {
  track: Track;
  index?: number;
  queue?: Track[];
  isFavorite?: boolean;
  onUnfavorite?: (id: string) => void;
}) {
  const { current, isPlaying, playTrack, setPlaying } = usePlayer();
  const isCurrent = current?.id === track.id;
  const [fav, setFav] = useState(!!initialFav);

  const toggleFav = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      if (fav) {
        await api.delete(`/api/favorites/${track.id}`);
        setFav(false);
        onUnfavorite?.(track.id);
      } else {
        await api.post("/api/favorites", { track_id: track.id });
        setFav(true);
        toast.success("Added to favorites");
      }
    } catch {
      toast.error("Could not update favorite");
    }
  };

  return (
    <div
      onClick={() => (isCurrent ? setPlaying(!isPlaying) : playTrack(track, queue))}
      className="group flex items-center gap-4 px-3 py-2.5 rounded-lg hover:bg-secondary cursor-pointer transition-colors"
    >
      <div className="w-6 text-sm text-muted-foreground tabular-nums shrink-0 flex items-center justify-center">
        {isCurrent && isPlaying ? (
          <Pause className="h-4 w-4 text-primary" />
        ) : (
          <>
            <span className="group-hover:hidden">{index !== undefined ? index + 1 : ""}</span>
            <Play className="h-4 w-4 hidden group-hover:block text-foreground" />
          </>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className={`text-sm truncate ${isCurrent ? "text-primary font-medium" : "font-medium"}`}>
          {track.title}
        </div>
        <div className="text-xs text-muted-foreground truncate">
          {track.album?.title || track.album_title || ""}
          {track.is_instrumental ? " · Instrumental" : ""}
        </div>
      </div>
      {initialFav !== undefined && (
        <button onClick={toggleFav} className="p-1.5 text-muted-foreground hover:text-destructive">
          <Heart className={`h-4 w-4 ${fav ? "fill-destructive text-destructive" : ""}`} />
        </button>
      )}
      <div className="text-xs text-muted-foreground tabular-nums w-12 text-right">{fmt(track.duration)}</div>
    </div>
  );
}
